'use client'

export type FreestarSlotResult = 'filled' | 'empty'

function resultForSlot(event: unknown, slotElementIds: readonly string[]): FreestarSlotResult | undefined {
    if (!event || typeof event !== 'object') {
        return undefined
    }

    const candidate = event as Partial<GooglePublisherTagSlotRenderEndedEvent>
    if (typeof candidate.isEmpty !== 'boolean' || !candidate.slot?.getSlotElementId) {
        return undefined
    }

    let eventSlotId: string
    try {
        eventSlotId = candidate.slot.getSlotElementId()
    } catch {
        return undefined
    }

    if (!slotElementIds.includes(eventSlotId)) {
        return undefined
    }

    return candidate.isEmpty ? 'empty' : 'filled'
}

export function subscribeToFreestarSlotResult(
    slotElementIds: readonly string[],
    onResult: (result: FreestarSlotResult) => void,
    target: Pick<Window, 'googletag'> = window
): () => void {
    let active = true
    let reportedEmpty = false
    let pubads: GooglePublisherTagPubAdsService | undefined

    const unsubscribe = () => {
        active = false
        if (!pubads) {
            return
        }

        const subscribedPubads = pubads
        pubads = undefined
        try {
            subscribedPubads.removeEventListener('slotRenderEnded', listener)
        } catch {
            // GPT cleanup is best-effort; the inactive guard still blocks delivery.
        }
    }

    const listener = (event: GooglePublisherTagSlotRenderEndedEvent) => {
        if (!active) {
            return
        }

        const result = resultForSlot(event, slotElementIds)
        if (!result) {
            return
        }

        if (result === 'empty') {
            if (reportedEmpty) {
                return
            }
            reportedEmpty = true
            onResult(result)
            return
        }

        unsubscribe()
        onResult(result)
    }

    const register = () => {
        if (!active) {
            return
        }

        try {
            const nextPubads = target.googletag?.pubads?.()
            if (!nextPubads) {
                return
            }

            pubads = nextPubads
            pubads.addEventListener('slotRenderEnded', listener)
        } catch {
            // Unsupported GPT surfaces must remain collapsed rather than imply a fill.
        }
    }

    try {
        const googletag = target.googletag || { cmd: [] }
        target.googletag = googletag
        googletag.cmd.push(register)
    } catch {
        // An unavailable GPT command queue produces no result.
    }

    return unsubscribe
}
